import type { SupabaseClient } from "@supabase/supabase-js";

export type DevAutoLoginEnv = {
  dev: boolean;
  enabled: unknown;
  email: unknown;
  password: unknown;
};

/** Credentials for a local email/password sign-in. Production builds pass `dev: false` and get nothing. */
export function devAutoLoginCredentials(env: DevAutoLoginEnv): { email: string; password: string } | null {
  if (!env.dev) return null;
  if (env.enabled !== "1") return null;
  const email = (typeof env.email === "string" ? env.email : "").trim().toLowerCase();
  const password = typeof env.password === "string" ? env.password : "";
  if (!email || !password) return null;
  return { email, password };
}

let attempted = false;

/**
 * Signs in once when the Vite dev server has VITE_DEV_AUTO_LOGIN=1 and no session is stored.
 * Existing sessions are left alone so a Google login in this browser is not replaced.
 */
export async function ensureDevAutoLogin(client: SupabaseClient): Promise<void> {
  if (attempted) return;
  if (!import.meta.env.DEV) return;
  const creds = devAutoLoginCredentials({
    dev: true,
    enabled: import.meta.env.VITE_DEV_AUTO_LOGIN,
    email: import.meta.env.VITE_DEV_LOGIN_EMAIL,
    password: import.meta.env.VITE_DEV_LOGIN_PASSWORD,
  });
  if (!creds) return;
  attempted = true;
  const { data, error: sessionError } = await client.auth.getSession();
  if (sessionError) {
    console.warn("[auth] dev auto-login session check failed", sessionError.message);
  }
  if (data.session) return;
  const { error } = await client.auth.signInWithPassword({
    email: creds.email,
    password: creds.password,
  });
  if (error) {
    console.warn("[auth] dev auto-login failed", error.message);
  }
}
